import {mutation, query} from "@/graphql/client";
import {gql} from "@urql/vue";
import {Absence, AbsenceType} from "@/graphql/generated/graphql";
import useEmitter, {useGlobalEmitter} from "@/helper/emitter";
import {kapitelDateString} from "@/graphql/kapitelTypes";
import {medkitOutline} from "ionicons/icons";
import date from "@/helper/datetime/date";
import {Icon} from "ionicons/dist/types/components/icon/icon";
import {KapitelIcon} from "@/helper/icon";
import {graphql} from "@/graphql/generated";

const emitter = useEmitter()

const orderedAbsenceTypes = [
    AbsenceType.Timeoff,
    AbsenceType.AzkAusgleich,
    AbsenceType.ExternalBooking,
    AbsenceType.Vacation,
    AbsenceType.Sickness
]

export const defaultAbsenceType = orderedAbsenceTypes[0]

export const absenceFragment = graphql(`fragment AbsenceFragment on Absence {
    id,
    date,
    type,
    comment,
    worktimeValuation
}`)

export const getLabelForAbsenceType = (absenceType: AbsenceType, abbreviated = false): string => {
    switch (absenceType) {
        case AbsenceType.Sickness:
            return  abbreviated ? 'K' : 'Krank'
        case AbsenceType.Timeoff:
            return  abbreviated ? '-' : 'Wunschfrei'
        case AbsenceType.Vacation:
            return  abbreviated ? 'U' : 'Urlaub'
        case AbsenceType.ExternalBooking:
            return  abbreviated ? 'D' : 'Externer Dienst'
        case AbsenceType.AzkAusgleich:
            return  abbreviated ? 'A' : 'AZK Ausgleich'
    }

    return  abbreviated ? '-' : 'Abwesend'
}

export const getIconForAbsenceType = (absenceType: AbsenceType, abbreviated = false): string | undefined => {
    switch (absenceType) {
        case AbsenceType.Sickness: return KapitelIcon.sick
        case AbsenceType.Timeoff: return undefined
        case AbsenceType.Vacation: return KapitelIcon.vacation
        case AbsenceType.ExternalBooking: return undefined
        case AbsenceType.AzkAusgleich: return KapitelIcon.azkAusgleich
    }
}


export function generateAbsence(
    date: kapitelDateString,
    type: AbsenceType
): Absence {
    return <Absence>{
        date: date,
        type,
    }
}

export async function fetchAbsencesForDate(date: kapitelDateString): Promise<any> {
    const result = await query(
        graphql(`
            query GetAbsence (
            $date: KapitelDateImmutable!
        ) {
            absence(date: $date) {
                ...AbsenceFragment
            }
        }`), {
            date: date
        }
    )

    const absences = result?.data?.absences;
    if (!absences) {
        throw new Error("no absences in absence response")
    }

    return absences
}



export async function fetchAbsencesForDateRange(begin: kapitelDateString, until: kapitelDateString): Promise<any> {
    const result = await query(
        graphql(`
            query GetAbsences (
                $min: KapitelDateImmutable!
                $max: KapitelDateImmutable!
            ) {
            absences(betweenDates: {max: $max, min: $min}) {
                ...AbsenceFragment
            }
        }`), {
                min: begin,
                max: until
            }
        )

    const absences = result?.data?.absences;
    if (!absences) {
        throw new Error("no absences in absence response")
    }

    return absences
}

export function getAbsenceTypes() {
    return Object.values(AbsenceType)
    // Object.keys(AbsenceType).filter(key => !isNaN(Number(AbsenceType[key])));
}

export const cmpAbsenceTypes = (at1:AbsenceType, at2:AbsenceType) => orderedAbsenceTypes.indexOf(at1) - orderedAbsenceTypes.indexOf(at2)

export async function setAbsence(absence: Absence) {

    const MUTATION = graphql(`
        mutation setAbsence(
            $date: KapitelDateImmutable!,
            $type: AbsenceType!,
            $worktimeValuation: Float,
            $comment: String
        ) {
            setAbsence(
                date: $date,
                type: $type,
                worktimeValuation: $worktimeValuation,
                comment: $comment
            ) {
                date
            }
        }`)

    const result = await mutation(
        MUTATION, {
            date: absence.date,
            type: absence.type,
            worktimeValuation: absence.worktimeValuation,
            comment: absence.comment
        }
    )

    useGlobalEmitter().emit('day:mutated', absence.date)

    const success = result?.data?.setAbsence?.date

    if (!success) {
        throw new Error("error while storing absence")
    }

    return success
}

export async function removeAbsence(absence: Absence) {

    const MUTATION = graphql(`
        mutation removeAbsence(
            $date: KapitelDateImmutable!
        ) {
            removeAbsence(
                date: $date
            )
        }`)

    const result = await mutation(
        MUTATION, {
            date: absence.date
        }
    )

    useGlobalEmitter().emit('day:mutated', absence.date)

    const success = result?.data

    if (!success) {
        throw new Error("error while removing absence")
    }

    return success
}


export function getAbsenceSummary(absences:Absence[]) {

    const typeList:Array<AbsenceType> = []
    absences.forEach(absence => {
        if (!typeList.includes(absence.type)) {
            typeList.push(absence.type)
        }
    })

    return {
        count: absences.length,
        typeList,
        typeLabelList: typeList.map(t => getLabelForAbsenceType(t)),

        plannedAbsenceCount: absences.filter(a => absencePlanningAbsenceTypes.includes(a.type)).length
    }
}


export const absencePlanningAbsenceTypes = [AbsenceType.AzkAusgleich, AbsenceType.Vacation]

export const isShortTermSicknessDay = (d: kapitelDateString) => {
    return date.isToday(d) || (date.isAfter(d, date.getToday()) && date.isBefore(d, date.addDays(date.getToday(), 7)))
}
